import { DetailedInsight, ExploreInsightDirection } from '@api';
import { getExploreDirection } from '@terminal/views/dashboard/whats-new-widget/components/insight-card/utils';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './InsightsDrawerContent.module.scss';

const KIND_LABEL_KEYS = {
  fundamentals: 'insights:tooltip.label.fundamentals',
  technicals: 'insights:tooltip.label.technicals',
  macroeconomics: 'insights:tooltip.label.macroeconomics',
  combination: 'insights:tooltip.label.combination',
} as const;

type KindLabelKey = keyof typeof KIND_LABEL_KEYS;

const isKindLabelKey = (value: string): value is KindLabelKey =>
  Object.hasOwn(KIND_LABEL_KEYS, value);

export interface InsightDrawerCategoryRowProps {
  insight: DetailedInsight;
}

export const InsightDrawerCategoryRow = ({
  insight,
}: InsightDrawerCategoryRowProps) => {
  const { t } = useTranslation(['widget', 'insights']);

  const line = useMemo(() => {
    const direction = getExploreDirection(insight.metadata.direction);
    const directionLabel =
      direction === ExploreInsightDirection.Bullish
        ? t('widget:insights.insightDirection_bullish')
        : t('widget:insights.insightDirection_bearish');

    const kind = insight.metadata.kind;
    const categoryKey =
      kind && isKindLabelKey(kind) ? KIND_LABEL_KEYS[kind] : undefined;
    const categoryLabel = categoryKey ? t(categoryKey) : null;

    return categoryLabel
      ? `${directionLabel} · ${categoryLabel}`
      : directionLabel;
  }, [insight.metadata.direction, insight.metadata.kind, t]);

  return (
    <p
      data-testid="insight-drawer-category-row"
      className={styles.insightCategoryRow}
    >
      {line}
    </p>
  );
};
